import React, { useCallback, useState } from 'react';
import { Search } from 'lucide-react';
import Button from '../shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './ToolkitProject';
import ToolkitFileStep, { STEP, stepIcon } from './ToolkitFileStep';
import ToolkitBatchTable from './ToolkitBatchTable';
import useToolkitBatch from './useToolkitBatch';
import '../OhipTransactionsPage.css';
import '../ToolkitBlocksPage.css';

// The shape most Toolkit screens share: get some rows, set a few options, process the rows one by one against the
// project's Opera Cloud connection. A screen supplies its words, its columns and either `processRow(row, ctx, opts)`
// or — so the rows can also Run on server — `request(row, opts)` + optional `interpret(result, row)` + `server`
// (useToolkitBatch); this supplies the project header, the source step, and the grid (Process all / Pause / per-row
// retry / step log / Excel / Run on server).
//
// THE ROWS COME FROM ONE OF:
//   a v5 report  — `kind` (+ fileTitle, fileLabel, accept): uploaded and parsed by files.js;
//   a query      — `fetchRows(ctx)` (+ queryTitle, queryForm, queryLabel): e.g. today's same-day departures.
//                  queryForm may be a function of ctx, for a form that itself reads Opera Cloud (room types).
//
// `options` renders between the source and the grid; `canRun` gates processing on them.

function ToolkitBatchPage({ title, subtitle, columns, searchKeys, exportName, processRow, request, interpret, server, options, canRun = true,
                            rowActions, prepareRows,
                            kind, fileTitle, fileLabel, accept,
                            fetchRows, queryTitle, queryForm, queryLabel = 'Find', canQuery = true }) {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const run = useCallback((row, opts) => processRow(row, ctx, opts), [processRow, ctx]);
    const batch = useToolkitBatch(request ? { request, interpret, ctx, server } : { processRow: run });
    const { load } = batch;
    const onRows = useCallback((rows) => load(prepareRows ? prepareRows(rows) : rows), [load, prepareRows]);
    const [query, setQuery] = useState({ state: STEP.IDLE, message: '' });

    const runQuery = async () => {
        setQuery({ state: STEP.BUSY, message: 'Fetching from Opera Cloud…' });
        try {
            const rows = await fetchRows(ctx);
            onRows(rows);
            setQuery({ state: STEP.DONE, message: `${rows.length} row(s) found.` });
        } catch (err) {
            setQuery({ state: STEP.ERROR, message: err?.response?.data?.message || err?.response?.data?.error?.message || err.message });
        }
    };

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title={title} subtitle={subtitle} />
            {fetchRows ? (
                <div className="tkb-step">
                    <div className="tkb-step-head"><span className="tkb-num">1</span> {queryTitle} {stepIcon(query.state)}</div>
                    <div className="tkb-row">
                        {typeof queryForm === 'function' ? queryForm(ctx) : queryForm}
                        <Button variant="secondary" onClick={runQuery} disabled={!ready || !canQuery || query.state === STEP.BUSY || batch.running}>
                            <Search className="icon" /> {queryLabel}
                        </Button>
                    </div>
                    {query.message && <p className={`tkb-msg${query.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{query.message}</p>}
                </div>
            ) : (
                <ToolkitFileStep number={1} title={fileTitle} label={fileLabel} accept={accept} kind={kind}
                    onParsed={onRows} disabled={!ready} />
            )}
            {options && (
                <div className="tkb-step">
                    <div className="tkb-step-head"><span className="tkb-num">2</span> Options</div>
                    {options}
                </div>
            )}
            <ToolkitBatchTable batch={batch} columns={columns} searchKeys={searchKeys} exportName={exportName}
                canRun={ready && canRun} onClear={() => load([])} rowActions={rowActions} />
        </div>
    );
}

export default ToolkitBatchPage;
